import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch, useApp } from '../bizsuite-app';
import { formatCurrencyShort } from '../utils/currency';
import { formatDate } from '../utils/date';

/* ================================================================
   Helpers
   ================================================================ */

function formatRelativeDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(d);
}

function pct(val) {
  return Math.min(100, Math.max(0, parseFloat(val) || 0));
}

const ACTIVITY_ICONS = {
  call: { cls: 'call', icon: '\u{1F4DE}' },
  email: { cls: 'email', icon: '\u{1F4E7}' },
  meeting: { cls: 'meeting', icon: '\u{1F4C5}' },
  note: { cls: 'note', icon: '\u{1F4DD}' }
};

/* ── Neon SVG Icons for Dashboard ── */
const svgIcon = (paths, color, size = 24) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {paths}
  </svg>
);
const DASH_ICONS = {
  revenue:  (c) => svgIcon(<><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></>, c),
  pipeline: (c) => svgIcon(<><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></>, c),
  util:     (c) => svgIcon(<><path d="M21.21 15.89A10 10 0 118 2.83"/><path d="M22 12A10 10 0 0012 2v10z"/></>, c),
  bench:    (c) => svgIcon(<><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></>, c),
  newLead:  (c) => svgIcon(<><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></>, c),
  invoice:  (c) => svgIcon(<><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1z"/><line x1="8" y1="8" x2="16" y2="8"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="8" y1="16" x2="12" y2="16"/></>, c),
  pipe:     (c) => svgIcon(<><rect x="4" y="3" width="16" height="18" rx="2"/><line x1="12" y1="3" x2="12" y2="21"/></>, c),
  salary:   (c) => svgIcon(<><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="3"/><line x1="2" y1="10" x2="4" y2="10"/><line x1="20" y1="14" x2="22" y2="14"/></>, c),
};

/* ================================================================
   Loading skeleton component
   ================================================================ */
function Skeleton({ width, height, style }) {
  return (
    <div
      style={{
        width: width || '100%',
        height: height || 20,
        background: 'linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite',
        borderRadius: 6,
        ...style,
      }}
    />
  );
}

/* ================================================================
   Summary Card
   ================================================================ */
function SummaryCard({ icon, iconColor, title, value, sub, loading, onClick }) {
  return (
    <div className={`summary-card${onClick ? ' clickable' : ''}`} onClick={onClick} title={onClick ? `View ${title} details` : undefined}>
      <div className={`card-icon ${iconColor}`}>{icon}</div>
      <div className="card-content">
        <h3>{title}</h3>
        <div className="value">{loading ? <Skeleton width={80} height={28} /> : value}</div>
        <div className="sub">{sub}</div>
      </div>
      {onClick && <svg className="card-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>}
    </div>
  );
}

/* ================================================================
   Horizontal Bar Chart (pure HTML/CSS)
   ================================================================ */
function HBarChart({ data, labelKey, valueKey, maxValue, formatValue, colorFn }) {
  if (!data || data.length === 0) {
    return <div style={{ color: '#94A3B8', textAlign: 'center', padding: 16 }}>No data available</div>;
  }
  const max = maxValue || Math.max(...data.map(d => parseFloat(d[valueKey]) || 0), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {data.map((item, idx) => {
        const val = parseFloat(item[valueKey]) || 0;
        const widthPct = (val / max) * 100;
        const color = colorFn ? colorFn(item, idx) : '#6366f1';
        const label = item[labelKey] || 'Unknown';
        const displayVal = formatValue ? formatValue(val) : val.toLocaleString();

        return (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 120, fontSize: 12, color: '#64748B', textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={label}>
              {label}
            </div>
            <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 4, height: 22, position: 'relative', overflow: 'hidden' }}>
              <div style={{ width: `${Math.max(widthPct, 2)}%`, background: color, height: '100%', borderRadius: 4, transition: 'width 0.6s ease' }} />
            </div>
            <div style={{ width: 70, fontSize: 12, fontWeight: 600, color: '#334155', textAlign: 'right', flexShrink: 0 }}>
              {displayVal}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ================================================================
   Utilization Gauge (circular)
   ================================================================ */
function UtilizationGauge({ value, size, label }) {
  const s = size || 140;
  const stroke = 10;
  const radius = (s - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const v = pct(value);
  const offset = circ - (v / 100) * circ;
  const color = v >= 75 ? '#10b981' : v >= 50 ? '#f59e0b' : '#ef4444';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <svg width={s} height={s} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={s / 2} cy={s / 2} r={radius} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
        <circle
          cx={s / 2} cy={s / 2} r={radius} fill="none"
          stroke={color} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.8s ease' }}
        />
      </svg>
      <div style={{ marginTop: -s / 2 - 16, fontSize: 28, fontWeight: 700, color: '#1e293b' }}>
        {Math.round(v)}%
      </div>
      <div style={{ marginTop: s / 2 - 24, fontSize: 12, color: '#64748B' }}>{label || 'Utilization'}</div>
    </div>
  );
}

/* ================================================================
   Receivables Aging Stacked Bar
   ================================================================ */
function AgingBar({ data }) {
  const bucketColors = {
    '0-30': '#10b981',
    '31-60': '#f59e0b',
    '61-90': '#f97316',
    '90+': '#ef4444',
  };
  const bucketOrder = ['0-30', '31-60', '61-90', '90+'];

  const mapped = {};
  for (const row of (data || [])) {
    const bucket = row.bucket || row.aging_bucket || 'unknown';
    const normalBucket = bucket.includes('0') && bucket.includes('30') && !bucket.includes('60') ? '0-30'
      : bucket.includes('31') || (bucket.includes('30') && bucket.includes('60')) ? '31-60'
      : bucket.includes('61') || (bucket.includes('60') && bucket.includes('90')) ? '61-90'
      : bucket.includes('90') || bucket.includes('+') ? '90+' : bucket;
    mapped[normalBucket] = (mapped[normalBucket] || 0) + (parseFloat(row.total_amount) || 0);
  }

  const total = Object.values(mapped).reduce((s, v) => s + v, 0);
  if (total === 0) {
    return <div style={{ color: '#94A3B8', textAlign: 'center', padding: 16 }}>No receivables data</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', height: 32 }}>
        {bucketOrder.map(bucket => {
          const val = mapped[bucket] || 0;
          const w = (val / total) * 100;
          if (w < 1) return null;
          return (
            <div
              key={bucket}
              style={{
                width: `${w}%`,
                background: bucketColors[bucket] || '#94A3B8',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 11, fontWeight: 600,
                transition: 'width 0.6s ease',
              }}
              title={`${bucket} days: ${formatCurrencyShort(val)}`}
            >
              {w > 10 ? formatCurrencyShort(val) : ''}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 8 }}>
        {bucketOrder.map(bucket => (
          <div key={bucket} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#64748B' }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: bucketColors[bucket] }} />
            {bucket} days
          </div>
        ))}
      </div>
    </div>
  );
}

/* ================================================================
   Main Management Dashboard
   ================================================================ */
/* ================================================================
   Drill-Down Detail View
   ================================================================ */
function DrillDownView({ title, onBack, children }) {
  return (
    <div>
      <button
        className="btn btn-secondary"
        onClick={onBack}
        style={{ marginBottom: 16, display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back to Dashboard
      </button>
      <div className="card">
        <div className="card-header"><h2>{title}</h2></div>
        <div className="card-body">{children}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const app = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drillDown, setDrillDown] = useState(null); // 'revenue' | 'pipeline' | 'utilization' | 'bench'

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await apiFetch('/api/dashboards/management');
      if (res.ok) {
        const json = await res.json();
        setData(json);
        setError(null);
      } else {
        throw new Error('Failed to load dashboard');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    const interval = setInterval(fetchDashboard, 300000); // 5 min refresh
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  const userName = app?.currentUser?.full_name?.split(' ')[0]
    || app?.currentUser?.fullName?.split(' ')[0]
    || 'there';

  const d = data || {};

  return (
    <div>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      <p style={{ color: '#64748B', marginBottom: 20, marginTop: -16 }}>
        Welcome back, {userName}. Here is your executive overview.
      </p>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: '12px 16px', borderRadius: 8, marginBottom: 16, color: '#dc2626', fontSize: 14 }}>
          {error} &mdash; <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={fetchDashboard}>Retry</span>
        </div>
      )}

      {/* Summary Cards */}
      <div className="summary-cards">
        <SummaryCard
          icon={DASH_ICONS.revenue('#00E5A0')} iconColor="neon-mint" title="Revenue YTD"
          value={formatCurrencyShort(d.revenue_ytd)} sub={`MTD: ${formatCurrencyShort(d.revenue_mtd)}`} loading={loading}
          onClick={() => setDrillDown('revenue')}
        />
        <SummaryCard
          icon={DASH_ICONS.pipeline('#3B8FF4')} iconColor="neon-blue" title="Pipeline Value"
          value={formatCurrencyShort(d.pipeline_total)} sub={`Weighted: ${formatCurrencyShort(d.pipeline_weighted)}`} loading={loading}
          onClick={() => setDrillDown('pipeline')}
        />
        <SummaryCard
          icon={DASH_ICONS.util('#FFB300')} iconColor="neon-gold" title="Utilization"
          value={`${d.utilization_pct || 0}%`} sub={`Win Rate: ${d.win_rate || 0}%`} loading={loading}
          onClick={() => setDrillDown('utilization')}
        />
        <SummaryCard
          icon={DASH_ICONS.bench('#BF5FFF')} iconColor="neon-violet" title="Bench Count"
          value={d.bench_count || 0} sub={`Avg ${d.avg_bench_days || 0} days`} loading={loading}
          onClick={() => setDrillDown('bench')}
        />
      </div>

      {/* Drill-down views */}
      {drillDown === 'revenue' && (
        <DrillDownView title="Revenue Details" onBack={() => setDrillDown(null)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
            <div style={{ background: 'rgba(16,185,129,0.08)', padding: 16, borderRadius: 8, textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>Revenue YTD</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#10b981' }}>{formatCurrencyShort(d.revenue_ytd)}</div>
            </div>
            <div style={{ background: 'rgba(99,102,241,0.08)', padding: 16, borderRadius: 8, textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>Revenue MTD</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#6366f1' }}>{formatCurrencyShort(d.revenue_mtd)}</div>
            </div>
            <div style={{ background: 'rgba(245,158,11,0.08)', padding: 16, borderRadius: 8, textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>Win Rate</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#f59e0b' }}>{d.win_rate || 0}%</div>
            </div>
          </div>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#334155' }}>Monthly Revenue Trend</h3>
          <HBarChart
            data={(d.revenue_trend || []).slice(-12)}
            labelKey="month"
            valueKey="revenue"
            formatValue={formatCurrencyShort}
            colorFn={() => '#10b981'}
          />
          {(d.top_customers || []).length > 0 && (
            <>
              <h3 style={{ fontSize: 14, fontWeight: 600, margin: '24px 0 12px', color: '#334155' }}>Top Customers by Revenue</h3>
              <HBarChart
                data={(d.top_customers || []).slice(0, 10)}
                labelKey="customer_name"
                valueKey="revenue"
                formatValue={formatCurrencyShort}
                colorFn={(_, idx) => ['#6366f1', '#8b5cf6', '#a855f7', '#c084fc', '#d4a5ff', '#6366f1', '#8b5cf6', '#a855f7', '#c084fc', '#d4a5ff'][idx]}
              />
            </>
          )}
        </DrillDownView>
      )}

      {drillDown === 'pipeline' && (
        <DrillDownView title="Pipeline Details" onBack={() => setDrillDown(null)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            <div style={{ background: 'rgba(99,102,241,0.08)', padding: 16, borderRadius: 8, textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>Total Pipeline</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#6366f1' }}>{formatCurrencyShort(d.pipeline_total)}</div>
            </div>
            <div style={{ background: 'rgba(16,185,129,0.08)', padding: 16, borderRadius: 8, textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>Weighted Pipeline</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#10b981' }}>{formatCurrencyShort(d.pipeline_weighted)}</div>
            </div>
          </div>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#334155' }}>Pipeline by Stage</h3>
          <HBarChart
            data={d.pipeline_by_stage || []}
            labelKey="stage"
            valueKey="total_value"
            formatValue={formatCurrencyShort}
            colorFn={(item) => {
              const colors = { Prospect: '#6366f1', Qualification: '#6366f1', Discovery: '#8b5cf6', Proposal: '#a855f7', Negotiation: '#f59e0b', 'Closed Won': '#10b981', 'Closed Lost': '#ef4444' };
              return colors[item.stage] || '#94A3B8';
            }}
          />
          {(d.pipeline_by_stage || []).length > 0 && (
            <table className="data-table" style={{ marginTop: 20, width: '100%' }}>
              <thead>
                <tr>
                  <th>Stage</th>
                  <th style={{ textAlign: 'right' }}>Deals</th>
                  <th style={{ textAlign: 'right' }}>Total Value</th>
                  <th style={{ textAlign: 'right' }}>Avg Deal Size</th>
                </tr>
              </thead>
              <tbody>
                {(d.pipeline_by_stage || []).map((s, idx) => (
                  <tr key={idx}>
                    <td>{s.stage}</td>
                    <td style={{ textAlign: 'right' }}>{s.count || 0}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrencyShort(s.total_value)}</td>
                    <td style={{ textAlign: 'right' }}>{s.count ? formatCurrencyShort((parseFloat(s.total_value) || 0) / s.count) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DrillDownView>
      )}

      {drillDown === 'utilization' && (
        <DrillDownView title="Utilization Details" onBack={() => setDrillDown(null)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <UtilizationGauge value={d.utilization_pct || 0} label="Billable Utilization" />
            </div>
            <div style={{ background: 'rgba(16,185,129,0.08)', padding: 16, borderRadius: 8, textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>Win Rate</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#10b981' }}>{d.win_rate || 0}%</div>
            </div>
            <div style={{ background: 'rgba(99,102,241,0.08)', padding: 16, borderRadius: 8, textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>Total Headcount</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#6366f1' }}>{d.headcount?.total || 0}</div>
              <div style={{ fontSize: 12, color: '#10b981', marginTop: 4 }}>+{d.headcount?.new_this_quarter || 0} new this quarter</div>
              <div style={{ fontSize: 12, color: '#ef4444' }}>-{d.headcount?.separated_this_quarter || 0} separated</div>
            </div>
          </div>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#334155' }}>Receivables Aging</h3>
          <AgingBar data={d.receivables_aging || []} />
        </DrillDownView>
      )}

      {drillDown === 'bench' && (
        <DrillDownView title="Bench Details" onBack={() => setDrillDown(null)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            <div style={{ background: 'rgba(139,92,246,0.08)', padding: 16, borderRadius: 8, textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>Bench Count</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#8b5cf6' }}>{d.bench_count || 0}</div>
            </div>
            <div style={{ background: 'rgba(245,158,11,0.08)', padding: 16, borderRadius: 8, textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>Avg Days on Bench</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#f59e0b' }}>{d.avg_bench_days || 0}</div>
            </div>
          </div>
          <p style={{ fontSize: 13, color: '#64748B', marginBottom: 12 }}>
            View detailed bench and resource data in the Resources module.
          </p>
          <button className="btn btn-primary" onClick={() => app?.navigate('/resources/bench')}>
            Go to Bench Pool
          </button>
        </DrillDownView>
      )}

      {/* Main dashboard content – hidden during drill-down */}
      {!drillDown && <>
      {/* Quick Actions */}
      <div className="quick-actions" style={{ marginBottom: 24 }}>
        <div className="quick-action" onClick={() => app?.navigate('/crm/leads')}>
          <span className="icon">{svgIcon(<><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></>, '#39FF14', 16)}</span> New Lead
        </div>
        <div className="quick-action" onClick={() => app?.navigate('/contracts')}>
          <span className="icon">{svgIcon(<><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1z"/><line x1="8" y1="8" x2="16" y2="8"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="8" y1="16" x2="12" y2="16"/></>, '#00F0FF', 16)}</span> New Invoice
        </div>
        <div className="quick-action" onClick={() => app?.navigate('/crm/pipeline')}>
          <span className="icon">{svgIcon(<><rect x="4" y="3" width="16" height="18" rx="2"/><line x1="12" y1="3" x2="12" y2="21"/></>, '#FF00E5', 16)}</span> View Pipeline
        </div>
        <div className="quick-action" onClick={() => app?.navigate('/employees')}>
          <span className="icon">{svgIcon(<><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="3"/><line x1="2" y1="10" x2="4" y2="10"/><line x1="20" y1="14" x2="22" y2="14"/></>, '#FFE500', 16)}</span> Process Salary
        </div>
      </div>

      {/* Two-column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>

        {/* Pipeline Funnel */}
        <div className="card">
          <div className="card-header"><h2>Pipeline by Stage</h2></div>
          <div className="card-body">
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[1, 2, 3, 4].map(i => <Skeleton key={i} height={22} />)}
              </div>
            ) : (
              <HBarChart
                data={d.pipeline_by_stage || []}
                labelKey="stage"
                valueKey="total_value"
                formatValue={formatCurrencyShort}
                colorFn={(item) => {
                  const colors = { Prospect: '#6366f1', Qualified: '#8b5cf6', Proposal: '#a855f7', Negotiation: '#f59e0b', Won: '#10b981', Lost: '#ef4444' };
                  return colors[item.stage] || '#94A3B8';
                }}
              />
            )}
          </div>
        </div>

        {/* Revenue Trend */}
        <div className="card">
          <div className="card-header"><h2>Revenue Trend</h2></div>
          <div className="card-body">
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[1, 2, 3, 4].map(i => <Skeleton key={i} height={22} />)}
              </div>
            ) : (
              <HBarChart
                data={(d.revenue_trend || []).slice(-6)}
                labelKey="month"
                valueKey="revenue"
                formatValue={formatCurrencyShort}
                colorFn={() => '#6366f1'}
              />
            )}
          </div>
        </div>

        {/* Utilization Gauge */}
        <div className="card">
          <div className="card-header"><h2>Utilization Rate</h2></div>
          <div className="card-body" style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', minHeight: 180 }}>
            {loading ? (
              <Skeleton width={140} height={140} style={{ borderRadius: '50%' }} />
            ) : (
              <>
                <UtilizationGauge value={d.utilization_pct || 0} label="Billable Utilization" />
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 13, color: '#64748B', marginBottom: 8 }}>Headcount</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#1e293b' }}>{d.headcount?.total || 0}</div>
                  <div style={{ fontSize: 12, color: '#10b981' }}>+{d.headcount?.new_this_quarter || 0} new</div>
                  <div style={{ fontSize: 12, color: '#ef4444' }}>-{d.headcount?.separated_this_quarter || 0} left</div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Receivables Aging */}
        <div className="card">
          <div className="card-header"><h2>Receivables Aging</h2></div>
          <div className="card-body">
            {loading ? (
              <Skeleton height={32} />
            ) : (
              <AgingBar data={d.receivables_aging || []} />
            )}
          </div>
        </div>
      </div>

      {/* Top Customers */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header"><h2>Top Customers by Revenue</h2></div>
        <div className="card-body">
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} height={22} />)}
            </div>
          ) : (
            <HBarChart
              data={(d.top_customers || []).slice(0, 5)}
              labelKey="customer_name"
              valueKey="revenue"
              formatValue={formatCurrencyShort}
              colorFn={(_, idx) => ['#6366f1', '#8b5cf6', '#a855f7', '#c084fc', '#d4a5ff'][idx] || '#94A3B8'}
            />
          )}
        </div>
      </div>

      {/* Recent Activities */}
      <div className="card">
        <div className="card-header"><h2>Recent Activities</h2></div>
        <div className="card-body">
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[1, 2, 3].map(i => (
                <div key={i} style={{ display: 'flex', gap: 12 }}>
                  <Skeleton width={36} height={36} style={{ borderRadius: '50%' }} />
                  <div style={{ flex: 1 }}>
                    <Skeleton width="60%" height={14} style={{ marginBottom: 4 }} />
                    <Skeleton width="40%" height={12} />
                  </div>
                </div>
              ))}
            </div>
          ) : (d.recent_activities || []).length === 0 ? (
            <div style={{ textAlign: 'center', padding: 24, color: '#94A3B8' }}>No recent activities</div>
          ) : (
            <ul className="activity-list">
              {(d.recent_activities || []).map((act, idx) => {
                const typeInfo = ACTIVITY_ICONS[act.type] || ACTIVITY_ICONS.note;
                return (
                  <li className="activity-item" key={idx}>
                    <div className={`activity-icon ${typeInfo.cls}`}>{typeInfo.icon}</div>
                    <div className="activity-content">
                      <div className="activity-title">{act.subject || act.type || 'Activity'}</div>
                      <div className="activity-desc">{act.notes || ''}</div>
                      <div className="activity-time">{formatRelativeDate(act.created_at || act.activity_date)}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
      </>}
    </div>
  );
}
